import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AccountProposal } from '@/hooks/account/use-account-proposals';
import { useCreateAccountProposal } from '@/hooks/admin/use-create-account-proposal';
import { useAccounts } from '@/hooks/account/useAccounts';
import {
  type DRPhase,
  useDisasterRecoverySettings,
} from '@/hooks/admin/organization/use-disaster-recovery-settings';
import { Link } from 'react-router';

import {
  type DisasterRecoveryProposalStatus,
  NetworkType,
  NETWORK_OPTIONS,
  createNetworkId,
} from '@mls-wallet/shared-types';
import {
  Section,
  SectionLeftColumn,
  SectionRightColumn,
  SectionTitle,
  SectionDescription,
  SectionRow,
} from '@/components/shared/SectionComponents';

interface NewAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProposalCreated: (proposal: AccountProposal) => void;
}

type DRNetworkRestrictionArgs = {
  drPhase: DRPhase | null;
  hasInitiationProposal: boolean;
  hasFinalizationProposal: boolean;
  finalizationProposalStatus: DisasterRecoveryProposalStatus | undefined;
};

/**
 * Restricts account-network expansion while DR setup is in-flight.
 *
 * We lock selection during initiation creation, setup timelock wait, and while
 * a finalization proposal exists but has not reached approved + executed.
 */
function shouldRestrictNetworksDuringDRSetup({
  drPhase,
  hasInitiationProposal,
  hasFinalizationProposal,
  finalizationProposalStatus,
}: DRNetworkRestrictionArgs): boolean {
  // Case: initiation proposal exists but setup is not on-chain yet.
  const isInitiationCreated = hasInitiationProposal && drPhase === null;
  // Case: setup was initiated on-chain and is waiting for setup timelock.
  const isInitiationInTimelock = drPhase === 'TIMELOCK_WAIT';
  // Case: finalization only unlocks when it is approved and executed.
  const isFinalizationApprovedAndExecuted =
    finalizationProposalStatus === 'APPROVED' && drPhase === 'COMPLETED';
  const hasBlockingFinalizationProposal =
    hasFinalizationProposal && !isFinalizationApprovedAndExecuted;

  return (
    isInitiationCreated ||
    isInitiationInTimelock ||
    hasBlockingFinalizationProposal
  );
}

export function NewAccountDialog({
  open,
  onOpenChange,
  onProposalCreated,
}: NewAccountDialogProps) {
  const [accountName, setAccountName] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<NetworkType[]>([]);
  const [pendingAddNetwork, setPendingAddNetwork] = useState<NetworkType | ''>(
    ''
  );
  const { createProposal, isCreating } = useCreateAccountProposal();
  const { accounts } = useAccounts();
  const { drConfig, drInitiationProposal, drFinalizationProposal } =
    useDisasterRecoverySettings();

  const outlineColor = 'border-slate-300 border';
  const organizationNetworkIds = useMemo(
    () => new Set(accounts.flatMap((account) => account.networks)),
    [accounts]
  );
  const finalizationProposal = drFinalizationProposal?.proposal;
  const shouldRestrictNetworkSelection = shouldRestrictNetworksDuringDRSetup({
    drPhase: drConfig?.phase ?? null,
    hasInitiationProposal: Boolean(drInitiationProposal?.proposal),
    hasFinalizationProposal: Boolean(finalizationProposal),
    finalizationProposalStatus: finalizationProposal?.status,
  });
  const availableNetworkOptions = useMemo(
    () =>
      shouldRestrictNetworkSelection
        ? NETWORK_OPTIONS.filter((network) =>
            organizationNetworkIds.has(network.id)
          )
        : NETWORK_OPTIONS,
    [organizationNetworkIds, shouldRestrictNetworkSelection]
  );
  const availableNetworkIds = useMemo(
    () => new Set(availableNetworkOptions.map((network) => network.id)),
    [availableNetworkOptions]
  );

  useEffect(() => {
    setSelectedNetworks((currentNetworks) => {
      const filteredNetworks = currentNetworks.filter((networkId) =>
        availableNetworkIds.has(networkId)
      );
      return filteredNetworks.length === currentNetworks.length
        ? currentNetworks
        : filteredNetworks;
    });
  }, [availableNetworkIds]);

  const addNetwork = (networkId: NetworkType) => {
    // Case: network is currently hidden due to DR setup restrictions.
    if (!availableNetworkIds.has(networkId)) {
      return;
    }

    setSelectedNetworks((currentNetworks) =>
      currentNetworks.includes(networkId)
        ? currentNetworks
        : [...currentNetworks, networkId]
    );
  };

  const removeNetwork = (networkId: NetworkType) => {
    setSelectedNetworks((currentNetworks) =>
      currentNetworks.filter((n) => n !== networkId)
    );
  };

  const handleSubmit = async () => {
    if (!accountName || selectedNetworks.length === 0) return;
    try {
      const proposal = await createProposal({
        name: accountName,
        networkIds: selectedNetworks,
      });
      onProposalCreated(proposal);
      onOpenChange(false);
      setAccountName('');
      setSelectedNetworks([]);
    } catch (e) {
      console.error('Failed to create account proposal', e);
      // Error is already handled by the hook and stored in createError
    }
  };

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      noPadding
      title="Create a new account"
      footer={
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          <Button
            size="lg"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={
              isCreating || !accountName || selectedNetworks.length === 0
            }
          >
            {isCreating ? 'Creating...' : 'Create Account'}
          </Button>
        </div>
      }
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6 max-w-3xl mx-auto w-full flex flex-col gap-8">
        {/* General */}
        <Section title="General">
          <SectionRow>
            <SectionLeftColumn>
              <SectionTitle>Account name</SectionTitle>
              <SectionDescription>
                What you want to name this cross-chain account
              </SectionDescription>
            </SectionLeftColumn>
            <SectionRightColumn>
              <Input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="Give your account a name"
                size="lg"
                className={`${outlineColor}`}
              />
            </SectionRightColumn>
          </SectionRow>
        </Section>

        {/* Networks */}
        <Section title="Blockchain Networks">
          <SectionRow>
            <SectionLeftColumn>
              <SectionTitle>Active Blockchain Networks</SectionTitle>
              <SectionDescription>
                Which blockchain networks this account will use
              </SectionDescription>
            </SectionLeftColumn>
            <SectionRightColumn>
              <div className="space-y-3">
                <Select
                  value={pendingAddNetwork ? String(pendingAddNetwork) : ''}
                  onValueChange={(v) => {
                    const id = createNetworkId(Number(v));
                    addNetwork(id);
                    setPendingAddNetwork('');
                  }}
                >
                  <SelectTrigger size="lg" className={`${outlineColor}`}>
                    <SelectValue placeholder="Choose blockchain networks" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableNetworkOptions.map((opt) => (
                      <SelectItem key={opt.id} value={String(opt.id)}>
                        {opt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {shouldRestrictNetworkSelection ? (
                  <p className="text-xs text-orange-600">
                    Some networks are hidden until{' '}
                    <Link
                      to="/settings"
                      className="text-orange-600 underline hover:opacity-90"
                    >
                      Disaster Recovery
                    </Link>{' '}
                    is set up
                  </p>
                ) : null}

                {selectedNetworks.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedNetworks.map((nid) => {
                      const opt = NETWORK_OPTIONS.find((o) => o.id === nid);
                      return (
                        <Badge
                          key={nid}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          <span>{opt?.name ?? nid}</span>
                          <span
                            className="ml-1 inline-flex items-center justify-center cursor-pointer hover:text-destructive"
                            onClick={() => removeNetwork(nid)}
                          >
                            ×
                          </span>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </SectionRightColumn>
          </SectionRow>
        </Section>
      </div>
    </NestedFullscreenDialog>
  );
}
